<template>
  <div>
    <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
      {{ t('app.subscription.renewal.step1_title') }}
    </h4>
    
    <div class="space-y-4">
      <!-- Plan Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {{ t('app.subscription.renewal.select_plan') }}
        </label>
        <select 
          :value="modelValue.selectedPlanId" 
          @input="$emit('update:selectedPlanId', Number(($event.target as HTMLSelectElement).value))"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option :value="null">
            {{ t('app.subscription.renewal.select_plan_placeholder') }}
          </option>
          <option
            v-for="plan in availablePlans"
            :key="plan.id"
            :value="plan.id"
          >
            {{ plan.display_name }} - ${{ plan.monthly_price }}/mes
          </option>
        </select>
      </div>

      <!-- Billing Cycle -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {{ t('app.subscription.renewal.billing_cycle') }}
        </label>
        <div class="flex gap-4">
          <label class="flex items-center">
            <input 
              :checked="modelValue.billingCycle === 'monthly'"
              @change="$emit('update:billingCycle', 'monthly')"
              type="radio" 
              value="monthly"
              class="mr-2"
            >
            <span class="text-gray-900 dark:text-white">{{ t('app.subscription.renewal.monthly') }}</span>
          </label>
          <label class="flex items-center">
            <input 
              :checked="modelValue.billingCycle === 'annual'"
              @change="$emit('update:billingCycle', 'annual')"
              type="radio" 
              value="annual"
              class="mr-2"
            >
            <span class="text-gray-900 dark:text-white">
              {{ t('app.subscription.renewal.annual') }}
              <span class="text-xs text-green-600 dark:text-green-400 ml-1">({{ t('app.subscription.renewal.discount') }})</span>
            </span>
          </label>
        </div>
      </div>

      <!-- Price Summary -->
      <div
        v-if="selectedPlan"
        class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
      >
        <div class="flex justify-between items-center">
          <span class="text-gray-700 dark:text-gray-300">{{ t('app.subscription.price') }}:</span>
          <span class="text-2xl font-bold text-blue-600 dark:text-blue-400">
            ${{ calculatedPrice }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface RenewalPlan {
  id: number
  display_name: string
  monthly_price: number
  annual_price: number
}

interface Props {
  availablePlans: RenewalPlan[]
  modelValue: {
    selectedPlanId: number | null
    billingCycle: 'monthly' | 'annual'
  }
}

const props = defineProps<Props>()

defineEmits<{
  'update:selectedPlanId': [value: number | null]
  'update:billingCycle': [value: 'monthly' | 'annual']
}>()

const { t } = useI18n()

// Debug logging
console.log('PlanSelectionStep - availablePlans:', props.availablePlans)

const selectedPlan = computed(() => {
  return props.availablePlans.find(p => p.id === props.modelValue.selectedPlanId)
})

const calculatedPrice = computed(() => {
  if (!selectedPlan.value) return 0
  return props.modelValue.billingCycle === 'monthly' 
    ? selectedPlan.value.monthly_price 
    : selectedPlan.value.annual_price
})
</script>
